import type { ComputedRef } from 'vue';
import type { Ref } from 'vue';

import { isTokenExpired } from './';
import { GemPagesService } from '../services/gempagesService';
import { app } from '@/store';
import { GraphQLClient } from 'graphql-request';

export const fetchFromAPI = async (endpoint: string, options?: RequestInit) => {
  try {
    const requestInit: RequestInit = {
      method: options?.method,
      headers: options?.headers,
      body: options?.body,
      ...options,
    };

    const response = await fetch(endpoint, requestInit);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(error.message);
    }
    throw new Error('Unknown error');
  }
};

// GraphQL client
const GP_V7_API_URL = import.meta.env.VITE_GP_V7_API_URL;
const GRAPHQL_URL = `${GP_V7_API_URL}graphql/query`;
let awaitRefreshToken: Promise<any> | null = null;

const requestMiddleware: any = async (request: Request) => {
  const shopStore = (app as any).$store;
  const gemSession = shopStore.getters['app/gemSession'];
  const shopInfoV7 = shopStore.getters['app/shopInfoV7'];

  const gemPagesService = new GemPagesService(GP_V7_API_URL as string);

  let token = shopInfoV7?.token;
  let shopID = shopInfoV7?.shopID;

  if (awaitRefreshToken) await awaitRefreshToken;

  if (isTokenExpired(token) && gemSession) {
    awaitRefreshToken = new Promise((resolve, reject) => {
      gemPagesService
        .fetchShopInfoV7(gemSession)
        .then((res) => {
          token = res?.token ?? '';
          shopID = res?.shopID ?? '';

          shopStore.commit('app/SET_SHOP_INFO_V7', { shopID, token });
          resolve(res);
        })
        .catch(() => {
          reject(null);
        });
    });
    await awaitRefreshToken;
    awaitRefreshToken = null;
  }

  // Customize headers
  const headers: Record<string, any> = {
    'Content-Type': 'application/json',
    Authorization: `Token ${token}`,
    'X-GemX-Shop-ID': shopID,
  };

  return {
    ...request,
    credentials: 'omit',
    headers,
  };
};

export const client = new GraphQLClient(GRAPHQL_URL, {
  credentials: 'include',
  requestMiddleware,
});

export function appFetcher<TData, TVariables>(
  query: string,
  variables?: TVariables | Ref<TVariables> | ComputedRef<TVariables>,
) {
  return async (): Promise<TData> => client.request<TData, any>(query, isRef(variables) ? variables.value : variables);
}
